import { AccordionVariant, AnswerPropertyKeys, ColumnType, SettingsElementType, } from '@thoughtspot/ts-chart-sdk';
import _ from 'lodash';
import { getName } from '../answer-column-adaptor.util';
import { isPropElement, transformPropElementToChartSettings, } from './custom-chart-schema-hydrator.util';
export var SettingsMenuItems;
(function (SettingsMenuItems) {
    SettingsMenuItems["CHART_TYPE"] = "CHART_TYPE";
    SettingsMenuItems["LAYOUT"] = "LAYOUT";
    SettingsMenuItems["COLUMN"] = "COLUMN";
    SettingsMenuItems["AXIS"] = "AXIS";
    SettingsMenuItems["DATA_LABEL"] = "DATA_LABEL";
    SettingsMenuItems["TOOLTIP"] = "TOOLTIP";
    SettingsMenuItems["LEGEND"] = "LEGEND";
    SettingsMenuItems["DISPLAY"] = "DISPLAY";
    SettingsMenuItems["QUERY_DETAILS"] = "QUERY_DETAILS";
    SettingsMenuItems["R_ANALYSIS"] = "R_ANALYSIS";
    SettingsMenuItems["CUSTOM_ACTION"] = "CUSTOM_ACTION";
})(SettingsMenuItems || (SettingsMenuItems = {}));
function safeTransformElement(element) {
    if (isPropElement(element)) {
        return transformPropElementToChartSettings(element);
    }
    return element;
}
const ChartPropertyPathMapping = {
    [AnswerPropertyKeys.NAME]: 'name',
    [AnswerPropertyKeys.SORT_CONFIG]: 'columnProps.columnProperties.sortConfig',
    [AnswerPropertyKeys.NUMBER_FORMATTER]: 'columnProps.columnProperties.format',
    [AnswerPropertyKeys.SORT_ORDER_VALUE]: 'aggregationType',
    [AnswerPropertyKeys.SORT_ORDER_VALUE_VALUE]: 'timeBucket',
    [AnswerPropertyKeys.BACKGROUND]: 'Background',
    [AnswerPropertyKeys.HANDLE_MISSING_VALUES]: 'handleMissingValues',
};
function removeTrailingDot(str) {
    if (str.endsWith('.')) {
        return str.slice(0, -1);
    }
    return str;
}
const updatedNestedElementsKey = (elements, parentKeyPrefix) => {
    elements.forEach((element) => {
        if (element.elementType === SettingsElementType.SORT_FORMAT) {
            return;
        }
        let newKey = '';
        if (parentKeyPrefix) {
            if (element.key) {
                newKey = `${parentKeyPrefix}${element.key}`;
            }
            else {
                newKey = removeTrailingDot(parentKeyPrefix);
            }
        }
        else {
            newKey = element.key ?? '';
        }
        if (element?.children) {
            updatedNestedElementsKey(element.children, newKey ? `${newKey}.` : '');
        }
        if (element?.key) {
            if (element?.isAnswerProperty) {
                const mappedKey = ChartPropertyPathMapping[element.key];
                if (mappedKey) {
                    _.set(element, 'key', mappedKey);
                }
                else {
                    _.set(element, 'key', newKey);
                }
            }
            else {
                _.set(element, 'key', newKey);
            }
        }
    });
};
function createColumnAnswerPropertySettings(isMeasure, allowedCustomConfigurations) {
    const answerSettings = [
        {
            elementType: SettingsElementType.COLUMN_RENAME_INPUT,
            key: ChartPropertyPathMapping.name,
            isAnswerProperty: true,
            properties: {
                value: '',
            },
            parseValueToComponent: ({ column }) => {
                return getName(column);
            },
        },
    ];
    if (isMeasure) {
        if (allowedCustomConfigurations?.allowColumnNumberFormatting) {
            answerSettings.push({
                elementType: SettingsElementType.NUMBER_FORMATTER,
                key: ChartPropertyPathMapping.numberFormatter,
                isAnswerProperty: true,
                properties: {
                    variant: 'font',
                    typeProps: {
                        ariaLabel: 'number-type',
                    },
                    subTypeProps: {
                        ariaLabel: 'number-subtype',
                    },
                    formatProps: {
                        ariaLabel: 'number-format',
                    },
                },
            });
        }
        if (allowedCustomConfigurations?.allowColumnConditionalFormatting) {
            answerSettings.push({
                elementType: SettingsElementType.CONDITIONAL_FORMATTER,
                isAnswerProperty: true,
            });
        }
    }
    return answerSettings;
}
function extractSectionalElement(element) {
    if (element.elementType === SettingsElementType.SECTION &&
        element.children.length === 1) {
        const cleanedElement = element.children[0];
        cleanedElement.properties.label =
            cleanedElement.properties.label ?? element.properties.label;
        cleanedElement.key = element.key;
        return cleanedElement;
    }
    return element;
}
export function transformColumnsVizPropDefinition(columnsVizPropDefinition, allowedCustomConfigurations) {
    if (!columnsVizPropDefinition || columnsVizPropDefinition.length === 0) {
        return { columnsVizPropDefinition: [] };
    }
    const transformedDefinitions = [];
    columnsVizPropDefinition.forEach(columnProp => {
        const isMeasure = columnProp.type === ColumnType.MEASURE;
        const answerSettings = createColumnAnswerPropertySettings(isMeasure, allowedCustomConfigurations);
        const columnDefinitions = Object.entries(columnProp.columnSettingsDefinition);
        columnDefinitions.forEach(([columnId, columnDef]) => {
            const isSettingsElementPresent = columnDef.elements.length > 0 &&
                !isPropElement(columnDef.elements[0]);
            if (isSettingsElementPresent) {
                const columnDefinition = {
                    [columnId]: {
                        settings: columnDef.elements,
                    },
                };
                transformedDefinitions.push(columnDefinition);
            }
            else {
                const transformedElements = columnDef.elements.map(element => safeTransformElement(element));
                const cleanedElements = transformedElements.map(element => extractSectionalElement(element));
                const columnDefinition = {
                    [columnId]: {
                        settings: cleanedElements,
                    },
                };
                columnDefinition[columnId].settings = [
                    {
                        elementType: SettingsElementType.SECTION,
                        children: [
                            {
                                elementType: SettingsElementType.ACCORDION,
                                properties: {
                                    variant: AccordionVariant.MINIMAL,
                                    firstItemExpanded: true,
                                },
                                children: [
                                    {
                                        elementType: SettingsElementType.ACCORDION_ITEM,
                                        key: 'columnName',
                                        runTimeProperties: {
                                            header: 'TS_INPUT',
                                        },
                                        properties: {
                                            header: 'TS_INPUT',
                                        },
                                        children: [
                                            ...answerSettings,
                                            ...columnDefinition[columnId]
                                                .settings,
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ];
                transformedDefinitions.push(columnDefinition);
            }
        });
    });
    return transformedDefinitions;
}
function transformGeneralElements(elements, pathPrefix) {
    if (!elements || elements.length === 0)
        return [];
    const clonedElements = _.cloneDeep(elements);
    updatedNestedElementsKey(clonedElements, pathPrefix);
    return clonedElements.map(element => safeTransformElement(element));
}
function createColumnSettings(columnsVizPropDefinition, allowedCustomConfigurations) {
    if (!columnsVizPropDefinition || columnsVizPropDefinition?.length === 0) {
        return [];
    }
    columnsVizPropDefinition?.forEach(schema => {
        Object.values(schema.columnSettingsDefinition).forEach((settings) => {
            updatedNestedElementsKey(settings?.elements ||
                [], '');
        });
    });
    const columnSettingsSchema = transformColumnsVizPropDefinition(columnsVizPropDefinition, allowedCustomConfigurations);
    return columnSettingsSchema;
}
function createDisplaySettings(elements) {
    if (!elements || elements.length === 0)
        return [];
    const displaySettings = transformGeneralElements(elements, '');
    return displaySettings;
}
function createTooltipSettings(elements) {
    if (!elements || elements.length === 0)
        return [];
    const tooltipSettings = transformGeneralElements(elements, '');
    return tooltipSettings;
}
function createDataLabelSettings(elements) {
    if (!elements || elements.length === 0)
        return [];
    const dataLabelSettings = transformGeneralElements(elements, '');
    return dataLabelSettings;
}
function createAxisSettings(elements) {
    if (!elements || elements.length === 0)
        return [];
    const axisSettings = transformGeneralElements(elements, '');
    return axisSettings;
}
function createLegendSettings(elements) {
    if (!elements || elements.length === 0)
        return [];
    const legendSettings = transformGeneralElements(elements, '');
    return legendSettings;
}
export function getCustomChartSettingsSchema(visualPropEditorDefinition, chartSettingItem, allowedCustomConfigurations) {
    if (!visualPropEditorDefinition)
        return [];
    const { elements, columnsVizPropDefinition, axisVizPropDefinition, dataLabelVizPropDefinition, legendVizPropDefinition, tooltipVizPropDefinition, displayVizPropDefinition, } = visualPropEditorDefinition;
    const settingsMap = () => {
        switch (chartSettingItem) {
            case SettingsMenuItems.COLUMN:
                return createColumnSettings(columnsVizPropDefinition, allowedCustomConfigurations);
            case SettingsMenuItems.AXIS:
                return createAxisSettings(axisVizPropDefinition);
            case SettingsMenuItems.DATA_LABEL:
                return createDataLabelSettings(dataLabelVizPropDefinition);
            case SettingsMenuItems.LEGEND:
                return createLegendSettings(legendVizPropDefinition);
            case SettingsMenuItems.TOOLTIP:
                return createTooltipSettings(tooltipVizPropDefinition);
            case SettingsMenuItems.DISPLAY:
                return createDisplaySettings(displayVizPropDefinition || elements);
            default:
                return [];
        }
    };
    return settingsMap();
}
export const getApplicableCustomSettings = (visualPropEditorDefinition) => {
    const alwaysApplicableSettings = [
        SettingsMenuItems.CHART_TYPE,
        SettingsMenuItems.LAYOUT,
        SettingsMenuItems.QUERY_DETAILS,
        SettingsMenuItems.CUSTOM_ACTION,
    ];
    if (!visualPropEditorDefinition) {
        return alwaysApplicableSettings;
    }
    const settingsPropertyMap = [
        {
            prop: 'columnsVizPropDefinition',
            menuItem: SettingsMenuItems.COLUMN,
        },
        { prop: 'axisVizPropDefinition', menuItem: SettingsMenuItems.AXIS },
        {
            prop: 'dataLabelVizPropDefinition',
            menuItem: SettingsMenuItems.DATA_LABEL,
        },
        { prop: 'legendVizPropDefinition', menuItem: SettingsMenuItems.LEGEND },
        {
            prop: 'tooltipVizPropDefinition',
            menuItem: SettingsMenuItems.TOOLTIP,
        },
    ];
    const categorySettings = settingsPropertyMap
        .filter(({ prop }) => !_.isEmpty(visualPropEditorDefinition[prop]))
        .map(({ menuItem }) => menuItem);
    if (!_.isEmpty(visualPropEditorDefinition.displayVizPropDefinition) ||
        !_.isEmpty(visualPropEditorDefinition.elements)) {
        categorySettings.push(SettingsMenuItems.DISPLAY);
    }
    return [...alwaysApplicableSettings, ...categorySettings];
};
//# sourceMappingURL=custom-chart-transformer.util.js.map